import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AssignmentHistoryListResponse } from '@cortex/domain-project-intake-contract';
import { assignmentHistoryApi } from '../lib/api.js';
import { queryKeys } from '../lib/query-keys.js';

/**
 * 案件アサイン履歴を取得するフック。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 案件アサイン履歴（案件番号・案件名・アサイン日・役割ごとのメンバー・アサイン意図）を TanStack Query で取得
 * @graph-connects assignmentHistoryApi.list [calls] アサイン履歴の一覧取得 API
 * @graph-connects queryKeys [calls] TanStack Query キー生成
 */
export function useAssignmentHistory() {
  return useQuery({
    queryKey: queryKeys.projects.assignmentHistory(),
    queryFn: () => assignmentHistoryApi.list(),
  });
}

/**
 * アサイン意図を保存するミューテーションフック。保存できたら一覧のキャッシュの該当行を
 * API が返した保存後の履歴で置き換える（全件を取り直さない）。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business アサイン履歴 1 件にアサイン意図を保存し、一覧の表示を保存後の値に更新する
 * @graph-connects assignmentHistoryApi.updateIntent [calls] アサイン意図の保存 API
 * @graph-connects queryKeys [calls] TanStack Query キー生成
 */
export function useUpdateAssignmentIntent() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, intent }: { id: string; intent: string }) =>
      assignmentHistoryApi.updateIntent(id, intent),
    onSuccess: (updated) => {
      queryClient.setQueryData<AssignmentHistoryListResponse>(
        queryKeys.projects.assignmentHistory(),
        (current) =>
          current && {
            ...current,
            items: current.items.map((item) => (item.id === updated.id ? updated : item)),
          },
      );
    },
  });
}
